import { useMemo, type ReactNode } from 'react';
import { msg, plural } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { RemoteUICopyProvider, type RemoteUICopy } from '@borgee/agent-remote-web/react';
import { errorMessage } from './errorMessages';

export const RemoteUIProvider = ({ children }: { children: ReactNode }) => {
  const { i18n } = useLingui();
  const copy = useMemo<RemoteUICopy>(() => ({
    showFullResult: i18n._(msg`Show full result`),
    showDetails: i18n._(msg`Show details`),
    rawResult: i18n._(msg`Raw result`),
    noResults: i18n._(msg`No results.`),
    fileChanges: i18n._(msg`File changes`),
    fileChangesPreview: i18n._(msg`File changes preview`),
    noFileChanges: i18n._(msg`No file changes reported.`),
    noDiffContent: i18n._(msg`No diff content provided.`),
    previousFile: i18n._(msg`from`),
    fileChangeKind: kind => i18n._({ added: msg`Added`, modified: msg`Modified`, deleted: msg`Deleted`, renamed: msg`Renamed`, unknown: msg`File change` }[kind]),
    diffStats: (added, deleted) => i18n._(msg`${added} added lines, ${deleted} deleted lines`),
    fileDiff: path => i18n._(msg`Diff for ${path}`),
    fileDiffPreview: path => i18n._(msg`Diff preview for ${path}`),
    moreFiles: count => i18n._(msg`${count} more files`),
    moreOutputBlocks: count => i18n._(msg`${count} more output blocks`),
    connectionUnavailable: i18n._(msg`Connection unavailable`),
    agentFailed: i18n._(msg`Agent failed`),
    executingCommand: i18n._(msg`Executing command`),
    agentActivity: i18n._(msg`Agent activity`),
    turnElapsedTime: i18n._(msg`Turn elapsed time`),
    interruptOperation: i18n._(msg`Interrupt the current native operation`),
    interruptUnavailable: i18n._(msg`Interrupt is unavailable for this Provider.`),
    subagents: i18n._(msg`Subagents`),
    subagentOpenFailed: i18n._(msg`This subagent could not be opened.`),
    opening: i18n._(msg`Opening…`),
    savedHistory: i18n._(msg`Saved history`),
    documentationLoadFailed: i18n._(msg`Unable to load skill documentation.`),
    closeSkillDetails: i18n._(msg`Close skill details`),
    loadingDocumentation: i18n._(msg`Loading documentation…`),
    descriptionOnly: i18n._(msg`This Provider exposes a description without full documentation.`),
    documentationUnavailable: i18n._(msg`Documentation is not available to preview.`),
    retryDocumentation: i18n._(msg`Retry documentation`),
    openAgentFirst: i18n._(msg`Open or attach to an Agent first.`),
    nativeCommandFailed: i18n._(msg`Native command failed.`),
    cancellationRequested: i18n._(msg`Cancellation requested.`),
    interruptFailed: i18n._(msg`Interrupt failed.`),
    unknownNativeCommand: i18n._(msg`Unknown or unavailable native command. Open / to refresh the command list.`),
    messageSent: i18n._(msg`Message sent.`),
    messageSteered: i18n._(msg`Input accepted for the current turn.`),
    steerCurrentTurn: i18n._(msg`Steer current turn`),
    steer: i18n._(msg`Steer`),
    steering: i18n._(msg`Steering…`),
    messageQueued: i18n._(msg`Message queued by Provider.`),
    agentCommandFailed: i18n._(msg`Agent command failed.`),
    liveProviderControls: i18n._(msg`Live provider controls`),
    loadingNativeCommands: i18n._(msg`Loading native commands…`),
    retry: i18n._(msg`Retry`),
    nativeCommandsUnavailable: i18n._(msg`This Provider does not expose native commands.`),
    noMatchingCommands: i18n._(msg`No matching native commands.`),
    nativeCommands: i18n._(msg`Native commands`),
    console: i18n._(msg`Console`),
    selectedSkill: i18n._(msg`Selected skill`),
    skillWaitForTurn: i18n._(msg`This Provider can invoke skills when the current turn finishes.`),
    message: i18n._(msg`Message`),
    messagePlaceholder: i18n._(msg`Message the Agent…`),
    openChatCommands: i18n._(msg`Open chat commands`),
    queueing: i18n._(msg`Queueing…`),
    queueNextTurn: i18n._(msg`Queue for next turn`),
    queueNativeHint: i18n._(msg`Let the native Provider handle this after the current turn`),
    queue: i18n._(msg`Queue`),
    interrupting: i18n._(msg`Interrupting…`),
    interruptRequested: i18n._(msg`Interrupt requested`),
    interrupt: i18n._(msg`Interrupt`),
    sending: i18n._(msg`Sending…`),
    sendMessage: i18n._(msg`Send message`),
    sendToActiveTurn: i18n._(msg`Send input to the active native turn`),
    startNativeTurn: i18n._(msg`Start a new native turn`),
    planningChangeFailed: i18n._(msg`Planning mode could not be changed.`),
    sessionPlanningMode: i18n._(msg`Session planning mode`),
    planning: i18n._(msg`Planning`),
    normalChat: i18n._(msg`Normal chat`),
    planningMode: i18n._(msg`Planning mode`),
    on: i18n._(msg`On`),
    off: i18n._(msg`Off`),
    planningUnsupported: i18n._(msg`Planning is not supported by this session.`),
    planningReadOnly: i18n._(msg`Planning mode is read-only for this session.`),
    waitingPlanningState: i18n._(msg`Waiting for Provider planning state.`),
    waitingProviderConfirmation: i18n._(msg`Waiting for Provider confirmation.`),
    planningChangeUnavailable: i18n._(msg`Planning can change only while connected and idle, with no pending interactions.`),
    planningConfirmed: i18n._(msg`Provider confirmed. Changes apply to the next message.`),
    settingChangeFailed: i18n._(msg`Session setting could not be changed.`),
    unavailable: i18n._(msg`Unavailable`),
    sessionControls: i18n._(msg`Session controls`),
    model: i18n._(msg`Model`),
    permissions: i18n._(msg`Permissions`),
    sessionStatusLabel: i18n._(msg`Status`),
    sessionStatusAndPlanning: i18n._(msg`Session status and planning`),
    sessionStatus: i18n._(msg`Session status`),
    modelSettings: i18n._(msg`Model settings`),
    permissionSettings: i18n._(msg`Permission settings`),
    closeSessionControls: i18n._(msg`Close session controls`),
    backToSessionControls: i18n._(msg`Back to session controls`),
    close: i18n._(msg`Close`),
    provider: i18n._(msg`Provider`),
    session: i18n._(msg`Session`),
    connection: i18n._(msg`Connection`),
    connected: i18n._(msg`Connected`),
    runtime: i18n._(msg`Runtime`),
    directory: i18n._(msg`Directory`),
    settingChangesDefault: i18n._(msg`Also changes the default for future sessions.`),
    settingsDisconnected: i18n._(msg`Disconnected. Values are last known; reconnect to change settings.`),
    settingsChangeUnavailable: i18n._(msg`Settings can change only while idle with no pending interactions.`),
    settingsApplyNextTurns: i18n._(msg`Changes apply to subsequent turns.`),
    settingsUnsupported: i18n._(msg`This Provider does not expose these session settings.`),
    openResource: i18n._(msg`Open resource`),
    downloadResource: i18n._(msg`Download resource`),
    loadingResource: i18n._(msg`Loading resource…`),
    loadResource: i18n._(msg`Load resource`),
    resourceRequestFailed: i18n._(msg`Resource request failed.`),
    referencedResources: i18n._(msg`Referenced resources`),
    resources: i18n._(msg`Resources`),
    informationRequest: i18n._(msg`INFORMATION REQUEST`),
    submit: i18n._(msg`Submit`),
    decline: i18n._(msg`Decline`),
    cancel: i18n._(msg`Cancel`),
    required: i18n._(msg`Required`),
    choose: i18n._(msg`Choose…`),
    yes: i18n._(msg`Yes`),
    no: i18n._(msg`No`),
    answerHiddenInHistory: i18n._(msg`This answer is hidden in conversation history.`),
    permissionRequest: i18n._(msg`PERMISSION REQUEST`),
    reviewRequestedAccess: i18n._(msg`Review requested access`),
    continueInBrowser: i18n._(msg`CONTINUE IN BROWSER`),
    externalLinkUnavailable: i18n._(msg`The requested link cannot be opened.`),
    returnAfterAction: i18n._(msg`Return here after completing the action.`),
    externalActionCompleted: i18n._(msg`I have completed this`),
    form: i18n._(msg`FORM`),
    submitted: i18n._(msg`Submitted`),
    declined: i18n._(msg`Declined`),
    notProvided: i18n._(msg`Not provided`),
    permissionReceipt: i18n._(msg`PERMISSIONS`),
    externalAction: i18n._(msg`EXTERNAL ACTION`),
    toolResult: i18n._(msg`Tool result`),
    result: i18n._(msg`Result`),
    noOutput: i18n._(msg`No output.`),
    output: i18n._(msg`Output`),
    resultTruncated: i18n._(msg`Result truncated to fit the timeline.`),
    nativeCommandsLoadFailed: i18n._(msg`Unable to load native commands.`),
    detailRendererUnavailable: i18n._(msg`Detail renderer unavailable`),
    agentTimeline: i18n._(msg`Agent timeline`),
    remoteSession: i18n._(msg`REMOTE SESSION`),
    connecting: i18n._(msg`Connecting`),
    noTimelineActivity: i18n._(msg`No timeline activity.`),
    sessionSubagents: i18n._(msg`Session subagents`),
    pendingInteractions: i18n._(msg`Pending interactions`),
    earlierActivityLoadFailed: i18n._(msg`Earlier activity could not be loaded.`),
    loadingEarlierActivity: i18n._(msg`Loading earlier activity…`),
    loadEarlierActivity: i18n._(msg`Load earlier activity`),
    runtimeStatus: (status) => ({ starting: i18n._(msg`Starting`), idle: i18n._(msg`Ready`), running: i18n._(msg`Working`), waiting: i18n._(msg`Waiting for response`), failed: i18n._(msg`Failed`), closed: i18n._(msg`Closed`) })[status],
    resourceStatus: (status) => ({ pending: i18n._(msg`Pending`), available: i18n._(msg`Available`), failed: i18n._(msg`Failed`), unavailable: i18n._(msg`Unavailable`) })[status],
    commandKind: (kind) => ({ skill: i18n._(msg`Skill`), command: i18n._(msg`Command`), prompt: i18n._(msg`Prompt`) })[kind],
    commandDetails: (kind) => kind === 'skill' ? i18n._(msg`Skill details`) : i18n._(msg`Command details`),
    viewSkill: (name) => i18n._(msg`View skill ${name}`),
    removeSkill: (name) => i18n._(msg`Remove skill ${name}`),
    composerKeyboardHint: (active) => active ? i18n._(msg`Enter to send now · Shift+Enter for a new line · / for commands`) : i18n._(msg`Enter to send · Shift+Enter for a new line · / for commands`),
    modelTitle: (value) => i18n._(msg`Model: ${value}`),
    permissionsTitle: (value) => i18n._(msg`Permissions: ${value}`),
    lastKnown: (value) => i18n._(msg`${value} (last known)`),
    byteCount: (count) => i18n._(msg`${count} bytes`),
    formValidation: (label, reason) => ({ required: i18n._(msg`${label} requires a value.`), range: i18n._(msg`${label} is outside the allowed range.`), selections: i18n._(msg`${label} has an invalid number of selections.`), length: i18n._(msg`${label} has an invalid length.`) })[reason],
    allowPermission: (scope) => scope === 'turn' ? i18n._(msg`Allow for turn`) : i18n._(msg`Allow for session`),
    allowedPermission: (scope) => scope === 'turn' ? i18n._(msg`Allowed · turn`) : i18n._(msg`Allowed · session`),
    permissionAccess: (access) => ({ read: i18n._(msg`read`), write: i18n._(msg`write`), deny: i18n._(msg`deny`), connect: i18n._(msg`connect`) })[access],
    permissionResource: (resource) => resource === 'filesystem' ? i18n._(msg`filesystem`) : i18n._(msg`network`),
    openExternalHost: (host) => i18n._(msg`Open ${host}`),
    exitCode: (code) => i18n._(msg`Exit code ${code}`),
    durationMs: (duration) => i18n._(msg`${duration} ms`),
    elapsedTime: (seconds) => {
      if (seconds < 60) return i18n._(msg`${seconds}s`);
      const minutes = Math.floor(seconds / 60);
      const remainder = String(seconds % 60).padStart(2, '0');
      if (minutes < 60) return i18n._(msg`${minutes}m ${remainder}s`);
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = String(minutes % 60).padStart(2, '0');
      return i18n._(msg`${hours}h ${remainingMinutes}m ${remainder}s`);
    },
    interactionUnavailable: i18n._(msg`Interaction unavailable`),
    invalidInteraction: i18n._(msg`Invalid interaction request`),
    missingApprovalScope: i18n._(msg`Allow was offered with no approval scope.`),
    unsupportedInteraction: i18n._(msg`Unsupported interaction`),
    unknownInteraction: i18n._(msg`Unknown interaction`),
    inputRequired: i18n._(msg`INPUT REQUIRED`),
    agentQuestions: i18n._(msg`Agent questions`),
    questions: i18n._(msg`Questions`),
    customResponse: i18n._(msg`Custom response`),
    previous: i18n._(msg`Previous`),
    next: i18n._(msg`Next`),
    submitting: i18n._(msg`Submitting…`),
    submitResponse: i18n._(msg`Submit response`),
    dismiss: i18n._(msg({ message: 'Dismiss', context: 'Interaction response' })),
    toolAuthorization: i18n._(msg`TOOL AUTHORIZATION`),
    deny: i18n._(msg`Deny`),
    planReview: i18n._(msg`PLAN REVIEW`),
    reviewProposedPlan: i18n._(msg`Review proposed plan`),
    revisionFeedback: i18n._(msg`Revision feedback`),
    planFeedbackPlaceholder: i18n._(msg`What should the Agent change?`),
    planFeedbackHint: i18n._(msg`Add feedback to request a revised plan.`),
    toolApproval: i18n._(msg`TOOL APPROVAL`),
    denied: i18n._(msg`Denied`),
    mismatchedInteraction: i18n._(msg`The recorded interaction response does not match its request.`),
    dismissed: i18n._(msg`Dismissed`),
    answered: i18n._(msg`Answered`),
    partiallyAnswered: i18n._(msg`Partially answered`),
    noAnswers: i18n._(msg`No answers`),
    hideQuestions: i18n._(msg`Hide questions`),
    showQuestions: i18n._(msg`Show questions`),
    noAnswerProvided: i18n._(msg`No answer provided`),
    reasoningTrace: i18n._(msg`Reasoning trace`),
    taskBoard: i18n._(msg`TASK BOARD`),
    executionPlan: i18n._(msg`Execution plan`),
    contextCompaction: i18n._(msg`Context compaction`),
    agentError: i18n._(msg`Agent error`),
    hiddenAnswer: i18n._(msg`Hidden answer`),
    runtimeNotice: i18n._(msg`Runtime notice`),
    markdownTable: i18n._(msg`Markdown table`),
    footnotes: i18n._(msg`Footnotes`),
    userMessage: i18n._(msg`User message`),
    assistantMessage: i18n._(msg`Assistant message`),
    you: i18n._(msg`You`),
    assistant: i18n._(msg`Assistant`),
    interactionUnavailableReason: (kind) => {
      switch (kind) {
        case 'question': return i18n._(msg`The host cannot respond to this question request.`);
        case 'plan_approval': return i18n._(msg`The host cannot respond to this plan approval request.`);
        case 'tool_approval': return i18n._(msg`The host cannot respond to this tool approval request.`);
        case 'form': return i18n._(msg`The host cannot respond to this form request.`);
        case 'permission_approval': return i18n._(msg`The host cannot respond to this permission approval request.`);
        case 'external_action': return i18n._(msg`The host cannot respond to this external action request.`);
      }
    },
    interactionResponseError: (error) => i18n._(errorMessage(error, msg`Interaction response failed.`)),
    answerRequired: (header) => i18n._(msg`${header} requires an answer.`),
    questionTab: (index, header, answered) => answered ? i18n._(msg`${index}. ${header}, answered`) : i18n._(msg`${index}. ${header}`),
    questionCount: (count) => i18n._(msg`${plural(count, { one: '# question', other: '# questions' })}`),
    answeredCount: (answered, total) => i18n._(msg`${answered} of ${total} answered`),
    allow: (scope) => scope === 'once' ? i18n._(msg`Allow once`) : i18n._(msg`Allow for session`),
    allowed: (scope) => scope === 'once' ? i18n._(msg`Allowed · once`) : i18n._(msg`Allowed · session`),
    planAction: (action, hasFeedback) => {
      switch (action) {
        case 'approve': return i18n._(msg`Approve`);
        case 'approve_and_resume': return i18n._(msg`Approve and execute`);
        case 'reject': return hasFeedback ? i18n._(msg`Request changes`) : i18n._(msg`Request revision`);
      }
    },
    planResult: (action) => {
      switch (action) {
        case 'approve': return i18n._(msg`Approved`);
        case 'approve_and_resume': return i18n._(msg`Approved and resumed`);
        case 'reject': return i18n._(msg`Rejected`);
      }
    },
    status: (status) => {
      switch (status) {
        case 'pending': return i18n._(msg`Pending`);
        case 'in_progress': return i18n._(msg`In progress`);
        case 'running': return i18n._(msg`Running`);
        case 'completed': return i18n._(msg`Completed`);
        case 'failed': return i18n._(msg`Failed`);
        case 'canceled': return i18n._(msg`Canceled`);
      }
    },
    toolDetail: (type) => {
      switch (type) {
        case 'read': return i18n._(msg`Read`);
        case 'edit': return i18n._(msg`Edit`);
        case 'write': return i18n._(msg`Write`);
        case 'search': return i18n._(msg`Query`);
        case 'fetch': return i18n._(msg`URL`);
      }
    },
    compactionTrigger: (trigger) => trigger === 'auto' ? i18n._(msg`Trigger: automatic`) : i18n._(msg`Trigger: manual`),
    footnoteBack: (referenceIndex, rereferenceIndex) => {
      const reference = referenceIndex + 1;
      return rereferenceIndex > 1 ? i18n._(msg`Back to reference ${reference}-${rereferenceIndex}`) : i18n._(msg`Back to reference ${reference}`);
    },
  }), [i18n]);
  return <RemoteUICopyProvider copy={copy}>{children}</RemoteUICopyProvider>;
};
